import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { WysiwygComponent } from "../utils/wysiwyg/wysiwyg.component"
import {
	booleanAttribute,
	Component,
	Input,
	OnInit,
	QueryList,
	ViewChild,
	ViewChildren,
	ChangeDetectionStrategy,
} from "@angular/core"
import {
	Document,
	documentTemaplateBtns,
	personTypes,
} from "@app/definitions/types"
import { GenAutocompleteComponent } from "@app/gen-inputs/gen-autocomplete/gen-autocomplete.component"
import { Modalable } from "@app/Modalable"
import * as moment from "moment/moment"
import {
	byId,
	dateDisplay,
	defaultEmptyString,
	defaultZero,
	flipYesNo,
	getIds,
	latest,
	oldest,
} from "@app/helpers"
import { GenSelectComponent } from "@app/gen-inputs/gen-select/gen-select.component"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { DatePickerComponent } from "@app/utils/date-picker/date-picker.component"
import {
	BtnExtComponent,
	CancelBtnComponent,
	Check,
	YesNo,
} from "@app/utils/utils.component"
import { ServerImgPipe } from "@app/utils/server-img.pipe"
import { TaExpandsComponent } from "@app/utils/ta-expands/ta-expands.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { PreviewFinresourceComponent } from "@app/preview-finresource/preview-finresource.component"
import { COMMON_IMPORTS } from "@app/common-imports"
import { RequireDefaultSignatureComponent } from "@app/require-fields/RequireCcField.component"
import { GenTableAddItemComponent } from "@app/gen-table-add-item/gen-table-add-item.component"
import { ConfirmComponent } from "@app/confirm/confirm.component"

const nameFieldArrs = {
	diagppatients: ["dx_status", "diagp_code", "diagp_name"],
	labtestpatients: ["labtest_name", "result"],
	medicpatients: [],
	nondrugpatients: ["nondrug_name"],
}
const langNames = {
	diagppatients: "diagppatients",
	labtestpatients: "labtests",
	medicpatients: "continue_treatment",
	nondrugpatients: "nondrugpatients",
}
const fpHtml = (fp) => `
  <div class="gap-1">
    <span style="font-weight:bold">${dateDisplay(fp.date)}</span>
    <br />
    ${fp.description}
  </div>
  <br />
`
const getFilteredFields = (items, fieldName) => {
	if (fieldName === "medicpatients") {
		const today = moment.utc().endOf("day")
		return items.filter((it) => !it.end_date || today.isBefore(it.end_date))
	}
	return fieldName === "diagppatients"
		? items.filter((it) => !it.end_date)
		: items
}
const getFieldContent = (item, fieldName) =>
	fieldName == "medicpatients"
		? `${item.medic_name} ${item.amount} X ${item.frequency} / ${item.frequency_unit} ${item.remarks ? `</br> ${item.remarks}` : ""}`
		: nameFieldArrs[fieldName].map((f) => item[f]).join(" ")

const getPatientEmailOrPhone = (contactways: any[], isPhone: boolean) => {
	const field = isPhone ? "phone" : "email"
	const items = contactways.filter((c) => c.contactway_identifier === field)
	return items.find((c) => c.main === "yes") || items.at(0)
}

const getPersonType = (item: any) => {
	if (item.patient_id) {
		return "patient"
	}
	if (item.payor_id) {
		return "payor"
	}
	if (item.contact_id) {
		return "contact"
	}
	if (item.user_id) {
		return "user"
	}
	return ""
}
const getPersonId = (item: any) =>
	item.patient_id || item.payor_id || item.contact_id || item.user_id || 0

function defaultNewDoc(value: any = null) {
	return value || new Document()
}

@Component({
	imports: [
		COMMON_IMPORTS,
		GenSelectComponent,
		LangValPipe,
		GenButtonGroupComponent,
		DatePickerComponent,
		GenAutocompleteComponent,
		GenInputComponent,
		Check,
		WysiwygComponent,
		YesNo,
		ServerImgPipe,
		TaExpandsComponent,
		CancelBtnComponent,
		BtnExtComponent,
		PreviewFinresourceComponent,
		RequireDefaultSignatureComponent,
		ModalWrapComponent,
		GenTableAddItemComponent,
		ConfirmComponent,
	],
	selector: "app-add-document",
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./add-document.component.html",
})
export class AddDocumentComponent extends Modalable {
	modalProps: any = null
	@Input({ transform: defaultNewDoc }) documentObj: any = new Document()
	@Input({ transform: booleanAttribute }) performValidation: boolean = false
	@Input({ transform: defaultZero }) patient_id: number = 0
	@Input({ transform: defaultZero }) payor_id: number = 0
	@Input({ transform: defaultZero }) user_id: number = 0
	@Input({ transform: defaultEmptyString }) title: string = ""
	@Input({ transform: defaultEmptyString }) preText: string = ""
	@Input() followup: any = null
	@Input({ transform: booleanAttribute }) initRunValidation: boolean = false
	@Input({ transform: booleanAttribute }) isMedicalLetter: boolean = false

	@ViewChild("htmlContent") htmlContent!: WysiwygComponent
	@ViewChildren(GenInputComponent) genInputs!: GenInputComponent[]
	errors: any = {} //will hold errors for the fields in the form
	isFormSubmittable: boolean = false //can submit (controls disabled in the submit button)
	followupImages: any[] = []
	isAllowed: boolean = false
	isDefaultSigOpen: boolean = true

	documenttemplates: any[] = []
	headtures: any[] = []
	isInPreview: boolean = false

	@ViewChild("contactFuzzyInput") contactFuzzyInput!: GenAutocompleteComponent

	saveStateInt: any = null
	ngOnDestroy() {
		clearInterval(this.saveStateInt)
	}
	async init() {
		this.isAllowed = true
		if (!this.documentObj.id) {
			this.documentObj.lang = this.lang.langCode
		}
		this.store.removeDecItem(this.documentObj)

		this.documentObj.medical_conf = this.store.cc().default_medical_conf

		if (this.documentObj.mode != "update") {
			this.documentObj = {
				...this.documentObj,
				followup_id: this.followup?.id || this.documentObj.followup_id,
				date: this.store.getToday(),
				title: this.documentObj.title || this.title,
				patient_id: this.patient_id,
				payor_id: this.payor_id,
				user_id: this.user_id,
			}
		}

		if (this.followup?.images) {
			this.followupImages = this.followup.images.map((it) => ({
				...it,
				isChecked: true,
			}))
		}

		this.loadDocumentTemplates()

		if (this.performValidation) {
			this.validateEntireForm()
		}
		await this.fetchHeadtures()
		if (this.documentObj.mode != "update") {
			this.documentObj.headture_id =
				this.headtures.filter((it) => it.header_default == "l").at(0)?.id || 0
			this.validateEntireForm()
		}
		if (this.permsService?.owner_has_users) {
			if (!this.headtures?.length) {
				this.modalProps = { name: "confirm-no-headtures" }
				return
			}
		}
		this.initStateSave()

		if (this.documentObj.followup_id && this.documentObj.id) {
			this.followupImages = await this.apiService.post("get_fp_images", {
				followup_id: this.documentObj.followup_id,
			})
			this.documentObj.image_ids?.split(",").forEach((image_id: number) => {
				const item = this.followupImages.find(byId(image_id))
				if (item) {
					item.isChecked = true
				}
			})
		}
		setTimeout(() => this.afterInit())
	}

	async afterInit() {
		if (this.documentObj.recipient) {
			await this.store.timeout(10)
			this.contactFuzzyInput.searchStr = this.documentObj.recipient
		}

		if (this.documentObj.mode != "update") {
			if (!this.htmlContent) {
				return
			}
			this.htmlContent.reset()

			if (this.isMedicalLetter) {
				await this.store.timeout(10)
				await this.loadMedicalLetter()
			}
			if (this.preText) {
				setTimeout(() => this.htmlContent.injectText(this.preText))
			}

			this.contactFuzzyInput.focus()
			await this.loadState()
		}
		if (this.initRunValidation) {
			this.validateEntireForm()
		}
	}

	handleContactChange(obj: any) {
		this.documentObj.recipient = obj?.name || ""
		this.validateEntireForm()
		this.choseRecipient()
	}
	handleContactKeyUp(obj: any) {
		this.documentObj.recipient = obj?.str || ""
		this.validateEntireForm()
	}

	async loadMedicalLetter() {
		const item = this.store
			.getSiteDataTable("documenttemplates")
			.find((it) => it.default_medical_letter === "yes")
		if (item) {
			this.documentObj.document_template = item.id
			await this.handleTemplateChange()
		} else {
			const fp = this.followup ? "[followup]" : ""
			await this.addHtml(`${fp}[diagppatients][labtestpatients][medicpatients]`)
		}
	}

	async loadState() {
		const { state } = await this.apiService.post("get_document_state", {
			person_type: getPersonType(this.documentObj),
			person_id: getPersonId(this.documentObj),
		})
		if (!state) {
			return
		}
		this.modalProps = { name: "confirm-replace-text", state }
	}

	initStateSave() {
		this.saveStateInt = setInterval(() => {
			this.apiService.post("save_document_state", {
				person_type: getPersonType(this.documentObj),
				person_id: getPersonId(this.documentObj),
				state: this.documentObj.html_content,
			})
		}, 10 * 1000)
	}
	checkImage(image: any) {
		image.isChecked = !image.isChecked
	}

	async openPreview() {
		if (this.isInPreview) {
			return null
		}
		this.isInPreview = true

		const checkedImgs = this.followupImages.filter(({ isChecked }) => isChecked)
		const imgExFields = !!checkedImgs.length && {
			images: checkedImgs,
			image_ids: getIds(checkedImgs).join(","),
		}

		const headtureRow = await this.prepHeadtureRow()
		this.documentObj = {
			...this.documentObj,
			...imgExFields,
			patient_id: this.patient_id,
			payor_id: this.payor_id,
			user_id: this.user_id,
			from: headtureRow?.header,
			headture_id: headtureRow?.id || 0,
		}

		this.isInPreview = false
		this.modalProps = {
			name: "preview-finresource",
			headtureRow,
			finresourceObj: { ...this.documentObj },
		}
	}
	closeIfValue(ret: any = null) {
		ret && this.onClose.emit(ret)
	}
	async prepHeadtureRow() {
		const exfields = this.permsService?.owner_has_users && {
			signature_text: [
				this.documentObj.default_signature,
				this.store.cc().default_signature,
			]
				.filter(Boolean)
				.join("\n"),
			signature_logo: "",
			prescription_img: this.store.cc().prescription_img,
		}
		const item = this.headtures.find(byId(this.documentObj.headture_id))
		return { ...item, ...exfields }
	}

	loadDocumentTemplates() {
		this.documenttemplates = this.store
			.getSiteDataTable("documenttemplates")
			.map((it) => ({ id: it.id, name: it.title }))
	}

	handleAddHeadtureClose(obj: any) {
		if (obj?.id) {
			this.headtures = [...this.headtures, { ...obj }]
		}
	}

	openAddContactModal() {
		this.modalProps = {
			name: "add-contact",
			fuzzyName: this.contactFuzzyInput.returnSearchStrForAdding(),
		}
	}
	handleCloseAddcontact(obj: any) {
		this.contactFuzzyInput.searchStr = obj.name
		this.chooseContact(obj)
	}

	async chooseContact(obj: any) {
		this.documentObj.contact_id = obj?.id || 0
		this.validateEntireForm()
	}

	validateEntireForm() {
		//validate all fields and detrmine if can be submitted (or has errors)
		const validationFields: any = {
			//field validations, control displaying field errors and ability to submit the form
			html_content: { not_empty: null },
			headture_id: { is_legit_fuzzy_value: true },
		}
		if (this.documentObj.show_title === "yes") {
			validationFields.title = { not_empty: null }
		}
		this.errors = {}
		this.isFormSubmittable = this.validator.validateEntireForm(
			this.documentObj,
			this.errors,
			validationFields
		)
	}
	choseRecipient() {
		const className = "titleClass"
		let find = this.genInputs.find(
			(inp) => inp.className.indexOf(className) != -1
		)
		if (find) {
			find.focus(true)
		}
	}

	replaceFieldPlaceholder(init: string, field: string, value: string) {
		const rep = `[${field}]`
		const repLang = `[${this.lang.getVal(field)}]`
		return init.replaceAll(rep, value).replaceAll(repLang, value)
	}
	futureMeetingsTable(rows: any[]) {
		return `
      <table style="width:100%" >
        <thead>
          <tr style="line-height:100%;" >
            <th>${this.lang.getVal("date")}</th>
            <th >${this.lang.getVal("day")}</th>
            <th >${this.lang.getVal("time")}</th>
          </tr>
        </thead>
        <tbody>
        ${rows
					.map((fus: any, index: number) => {
						const time = fus.time.split(":").slice(0, 2).join(":")
						const bg = index % 2 ? "" : "background-color:#F5F5F5;"
						return `<tr style="line-height:100%;${bg} ">
              <td>${fus.meetingDate}</td>
              <td>${fus.dayName}</td>
              <td>${time}</td>
            </tr>`
					})
					.join("")}
        </tbody>
      </table>
    `
	}
	async fieldReplace(fieldName: string, patient: any) {
		switch (fieldName) {
			case "marital_status":
				return this.lang.getVal(patient[fieldName])
			case "name":
      case "name_en":
      case "id_number":
			case "address":
			case "age":
			case "remarks":
				return patient[fieldName]
			case "payor":
				return this.store.searchSiteDataItem(patient.payor_id, ["payors"])?.name
			case "email":
			case "phone":
				return getPatientEmailOrPhone(
					patient.contactways,
					fieldName === "phone"
				)?.contactway
			case "future_meetings":
				const rows: any = await this.store.getFutureFuses(this.patient_id)
				return rows?.length && this.futureMeetingsTable(rows)
			case "followup":
				const fp =
					this.followup ||
					patient.mixed_list
						.filter((it) => it.type == "followup")
						.toSorted(latest)
						.at(0)
				return fp && `<div>${dateDisplay(fp.date)}</div>${fp.description}`
			case "followups":
				return patient.mixed_list
					.filter((it) => it.type == "followup")
					.toSorted(oldest)
					.map(fpHtml)
					.join("")
			default:
				const arr = getFilteredFields(patient[fieldName] || [], fieldName)
				return (
					arr?.length &&
					`
          <div>${this.lang.getVal(langNames[fieldName])}</div>
          ${arr.map((it) => `<div style='direction:ltr;'>${getFieldContent(it, fieldName)}</div>`).join("")}
        `
				)
		}
	}

	async addHtml(html: string) {
		const patient: any = this.store.data.loadedPatients.find(
			byId(this.patient_id)
		)
		if (!patient) {
			this.htmlContent.addText(html)
			return
		}
		let html_content = this.store
			.getPreppedPatientFields()
			.reduce(
				(acc, { id, name }) =>
					this.replaceFieldPlaceholder(
						acc,
						name,
						patient[`patientfield_id_${id}`] || ""
					),
				html
			)
		for (let fieldName of documentTemaplateBtns) {
			const value = (await this.fieldReplace(fieldName, patient)) || ""
			html_content = this.replaceFieldPlaceholder(
				html_content,
				fieldName,
				value
			)
		}

		this.htmlContent.addText(html_content)
	}

	async handleTemplateChange() {
		const item = this.store.searchSiteDataItem(
			this.documentObj?.document_template,
			["documenttemplates"]
		)
		if (item) {
			this.addHtml(item.html_content)
		}
	}
	flipShowTitle() {
		this.documentObj.show_title = flipYesNo(this.documentObj.show_title)
		this.validateEntireForm()
	}
	async handleLangChange() {
		this.documentObj.headture_id = 0
		this.headtures = []
		this.validateEntireForm()
		this.fetchHeadtures()
	}
	async fetchHeadtures() {
		const res: any = await this.apiService.get_gen_items("headtures", {
			filterField: "header_default",
			filterFieldValue: "l",
			filterFieldApiObject: {
				lang: this.documentObj.lang,
			},
		})
		this.headtures = res.headtures.toSorted((a, b) => a.id - b.id)
	}
}
